import { Platform } from 'react-native';
import { LAYOUT_BREAKPOINTS } from '../theme/layoutBreakpoints';

type AppPlatform = 'ios' | 'android' | 'web' | 'windows' | 'macos';
export type TabletPortraitNavLayout = 'native' | 'sidebar';

/**
 * Web breakpoint for sidebar (matches BackOfficeSidebarLayout / WebSidebarLayout).
 * Sidebar shows when width >= this on web.
 */
export const WEB_SIDEBAR_BREAKPOINT = 768;

/**
 * Determines if the app should use sidebar navigation instead of bottom tabs.
 * Used by BackOffice and will be used by employee flow.
 *
 * - Web: sidebar when width >= 768
 * - Native: sidebar when tablet landscape (width >= 840 and width > height)
 *
 * Single source of truth for this logic. Components should not duplicate
 * Platform.OS checks for sidebar vs bottom tab decisions.
 */
export function shouldUseSidebarNav(
  width: number,
  height: number,
  platform: AppPlatform = Platform.OS,
): boolean {
  if (platform === 'web') {
    return width >= WEB_SIDEBAR_BREAKPOINT;
  }
  const isLandscape = width > height;
  const isTabletLandscape =
    width >= LAYOUT_BREAKPOINTS.expandedMin && isLandscape;
  return isTabletLandscape;
}

export function isNativeTabletPortrait(
  width: number,
  height: number,
  platform: AppPlatform = Platform.OS,
): boolean {
  if (platform === 'web') return false;
  const isPortrait = height > width;
  const shortEdge = Math.min(width, height);
  return isPortrait && shortEdge >= LAYOUT_BREAKPOINTS.mediumMin;
}

export function shouldShowTabletSidebarLayoutSetting(
  width: number,
  height: number,
  platform: AppPlatform = Platform.OS,
): boolean {
  return isNativeTabletPortrait(width, height, platform);
}

export function getEffectiveSidebarNav(
  width: number,
  height: number,
  tabletPortraitNavLayout: TabletPortraitNavLayout,
  platform: AppPlatform = Platform.OS,
): boolean {
  if (platform === 'web') return width >= WEB_SIDEBAR_BREAKPOINT;
  if (shouldUseSidebarNav(width, height, platform)) return true;
  if (isNativeTabletPortrait(width, height, platform)) {
    return tabletPortraitNavLayout === 'sidebar';
  }
  return false;
}

/**
 * Use web-style layout (larger buttons, web qty controls, etc.).
 * True when: web, OR native tablet landscape (sidebar mode).
 * Use for CartScreen, forms, and other screens that have a "web layout" variant.
 */
export function shouldUseWebLayout(
  width: number,
  height: number,
  platform: AppPlatform = Platform.OS,
): boolean {
  if (platform === 'web') return true;
  return shouldUseSidebarNav(width, height, platform);
}

/**
 * Use expanded/wide layout (multi-column, larger elements, more horizontal space).
 * True when: web, OR native tablet landscape.
 * Use for WelcomeScreen, auth/landing screens, and any content with compact vs expanded variants.
 * Documents intent for screens that adapt layout by available space, not platform alone.
 */
export function shouldUseExpandedLayout(
  width: number,
  height: number,
  platform: AppPlatform = Platform.OS,
): boolean {
  return shouldUseWebLayout(width, height, platform);
}

/**
 * Apply max-width constraint ({@link WEB_MAX_CONTENT_WIDTH} centered container).
 * True on web (any viewport), or native tablet landscape — same as {@link shouldUseWebLayout}.
 */
export function shouldUseWidthConstraint(
  width: number,
  height: number,
  platform: AppPlatform = Platform.OS,
): boolean {
  return shouldUseWebLayout(width, height, platform);
}

/**
 * Product grid column count for web/sidebar layout.
 * Matches ProductList.web breakpoints. Configurable via optional override.
 */
export function getProductGridNumColumns(
  width: number,
  override?: number,
): number {
  if (typeof override === 'number' && override > 0) return override;
  if (width >= 1400) return 6;
  if (width >= 1200) return 5;
  if (width >= 900) return 4;
  if (width >= 640) return 3;
  return 2;
}

/**
 * Product "row layout" column count for web/sidebar layout.
 * Row cards have a fixed controls width, so keep columns conservative.
 */
export function getProductRowNumColumns(
  width: number,
  override?: number,
): number {
  if (typeof override === 'number' && override > 0) return override;
  if (width >= 1280) return 3;
  if (width >= 920) return 2;
  return 1;
}
